/**
 * /moje/obserwowane — strefa klienta z 3 tabami:
 * ❤️ Hodowle | 🐶 Szczenięta | 🔔 Wyszukiwania
 *
 * Każdy tab ma:
 *  - listę kart z entity-specific CTA
 *  - dedykowany empty state z linkiem konwersji
 *
 * API: /api/public/favorites/enriched (kennels, litters, studs)
 *      + listSavedSearches() (lokalne saved-searches)
 */
import { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  Heart, Bell, Search, Loader2, MapPin, Trash2, ArrowRight, Plus, Phone, MessageCircle, Dog,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import TopNavBar from '@/components/TopNavBar';
import ApisAIFooter from '@/components/ApisAIFooter';
import PushToggle from '@/components/PushToggle';
import { API_BASE as API } from '@/utils/api';
import { clientHeaders } from '@/utils/marketplaceEvents';
import { listSavedSearches, deleteSavedSearch } from '@/utils/savedSearches';

const TABS = [
  { key: 'kennels', label: 'Hodowle', icon: Heart, dataKey: 'kennels' },
  { key: 'litters', label: 'Szczenięta', icon: Dog, dataKey: 'litters' },
  { key: 'searches', label: 'Wyszukiwania', icon: Bell, dataKey: 'searches' },
];

function CardKennel({ item }) {
  const initials = (item.title || 'H').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  return (
    <article
      className="rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition p-4 flex gap-4"
      data-testid={`obserwowane-kennel-${item.slug}`}
    >
      {item.image ? (
        <img src={item.image} alt={item.title} loading="lazy"
             className="w-20 h-20 rounded-xl object-cover bg-white/5 shrink-0" />
      ) : (
        <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-indigo-500/30 to-rose-500/30 flex items-center justify-center text-white font-black text-xl shrink-0">
          {initials}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-semibold text-white truncate">{item.title}</h3>
        <div className="text-xs text-gray-400 inline-flex items-center gap-1 mt-1">
          <MapPin className="w-3 h-3" />
          {[item.city, item.voivodeship].filter(Boolean).join(' · ') || 'Polska'}
        </div>
        {item.breeds?.length > 0 && (
          <div className="text-xs text-gray-500 mt-1 truncate">{item.breeds.slice(0, 3).join(', ')}</div>
        )}
        <div className="flex gap-2 mt-2.5 flex-wrap">
          <Link to={item.url}>
            <Button size="sm" variant="outline"
                    className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5 text-xs h-8"
                    data-testid={`obserwowane-kennel-${item.slug}-view`}>
              Zobacz hodowlę <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
          {item.phone && (
            <a href={`tel:${item.phone}`} data-testid={`obserwowane-kennel-${item.slug}-phone`}>
              <Button size="sm" className="rounded-full bg-emerald-500 text-black hover:bg-emerald-400 text-xs h-8 font-semibold">
                <Phone className="w-3 h-3 mr-1" />Zadzwoń
              </Button>
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

function CardLitter({ item }) {
  return (
    <article
      className="rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition p-4 flex gap-4"
      data-testid={`obserwowane-litter-${item.slug}`}
    >
      {item.image ? (
        <img src={item.image} alt={item.title} loading="lazy"
             className="w-20 h-20 rounded-xl object-cover bg-white/5 shrink-0" />
      ) : (
        <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-amber-500/30 to-rose-500/30 flex items-center justify-center text-2xl shrink-0">
          🐕
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-semibold text-white truncate">{item.breed || item.title}</h3>
        <div className="text-xs text-gray-400 inline-flex items-center gap-1 mt-1">
          <MapPin className="w-3 h-3" />
          {item.city || 'Polska'} {item.kennel_name && <span>· {item.kennel_name}</span>}
        </div>
        {item.price && (
          <div className="text-xs text-emerald-300 font-semibold mt-1">od {item.price} zł</div>
        )}
        <div className="flex gap-2 mt-2.5 flex-wrap">
          <Link to={item.url}>
            <Button size="sm" variant="outline"
                    className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5 text-xs h-8"
                    data-testid={`obserwowane-litter-${item.slug}-view`}>
              Zobacz miot <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
          {item.whatsapp && (
            <a href={item.whatsapp} target="_blank" rel="noreferrer"
               data-testid={`obserwowane-litter-${item.slug}-whatsapp`}>
              <Button size="sm" className="rounded-full bg-green-500 text-black hover:bg-green-400 text-xs h-8 font-semibold">
                <MessageCircle className="w-3 h-3 mr-1" />Zapytaj
              </Button>
            </a>
          )}
          {!item.whatsapp && item.phone && (
            <a href={`tel:${item.phone}`}>
              <Button size="sm" className="rounded-full bg-emerald-500 text-black hover:bg-emerald-400 text-xs h-8 font-semibold">
                <Phone className="w-3 h-3 mr-1" />Zadzwoń
              </Button>
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

function CardSearch({ search, onDelete }) {
  const summary = [
    search.type && search.type !== 'all' ? search.type.replace('mioty', 'szczenięta') : null,
    search.rasa,
    search.miasto || search.wojewodztwo,
  ].filter(Boolean).join(' · ') || 'wszystkie oferty';
  return (
    <article
      className="rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition p-4 flex items-center justify-between gap-4"
      data-testid={`obserwowane-search-${search.id}`}
    >
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-semibold text-white truncate">{search.name}</h3>
        <div className="text-xs text-gray-400 mt-0.5">{summary}</div>
        {search.notify_email && (
          <div className="text-xs text-blue-300 mt-1 inline-flex items-center gap-1">
            <Bell className="w-3 h-3" /> Powiadomienia: {search.notify_email}
          </div>
        )}
      </div>
      <div className="flex gap-2 shrink-0">
        <Link to="/moje/wyszukiwania">
          <Button size="sm" variant="outline"
                  className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5 text-xs h-8"
                  data-testid={`obserwowane-search-${search.id}-edit`}>
            Edytuj
          </Button>
        </Link>
        <Button
          size="sm" variant="outline"
          onClick={() => { if (window.confirm('Usunąć to wyszukiwanie?')) onDelete(search.id); }}
          className="rounded-full border-rose-500/30 bg-transparent text-rose-300 hover:bg-rose-500/10 text-xs h-8"
          data-testid={`obserwowane-search-${search.id}-delete`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </article>
  );
}

function EmptyState({ icon: Icon, title, copy, ctaLabel, ctaTo, testId }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/15 p-12 text-center" data-testid={testId}>
      <Icon className="w-10 h-10 text-gray-600 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
      <p className="text-gray-400 max-w-md mx-auto mb-6">{copy}</p>
      <Link to={ctaTo}>
        <Button className="rounded-full bg-white text-black hover:bg-white/90 font-semibold px-6 h-11">
          <Plus className="w-4 h-4 mr-1.5" />
          {ctaLabel}
        </Button>
      </Link>
    </div>
  );
}

export default function MojeObserwowane() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = TABS.find(t => t.key === params.get('tab'))?.key || 'kennels';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [favs, setFavs] = useState({ kennels: [], litters: [], studs: [] });
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [favRes, savedRes] = await Promise.all([
          axios.get(`${API}/api/public/favorites/enriched`, { headers: clientHeaders(), timeout: 8000 })
            .then(r => r.data).catch(() => ({ kennels: [], litters: [], studs: [] })),
          listSavedSearches().catch(() => []),
        ]);
        if (!alive) return;
        setFavs({
          kennels: favRes.kennels || [],
          litters: favRes.litters || [],
          studs: favRes.studs || [],
        });
        setSearches(savedRes || []);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const counts = useMemo(() => ({
    kennels: favs.kennels.length,
    litters: favs.litters.length + favs.studs.length,
    searches: searches.length,
  }), [favs, searches]);

  const switchTab = (key) => {
    setActiveTab(key);
    setParams({ tab: key }, { replace: true });
  };

  const handleDeleteSearch = async (id) => {
    try {
      await deleteSavedSearch(id);
      setSearches(prev => prev.filter(s => s.id !== id));
    } catch { /* silent */ }
  };

  return (
    <>
      <Helmet>
        <title>Moje obserwowane — ApisAI</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-[#0a0f1e] via-[#0e1628] to-[#0a0f1e] text-white">
        <TopNavBar />

        <section className="max-w-4xl mx-auto px-6 pt-16 pb-12">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300 text-xs mb-4">
              <Heart className="w-3.5 h-3.5 text-rose-400 fill-rose-400" />
              Moje konto
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-3">
              Obserwowane
            </h1>
            <p className="text-gray-400 max-w-xl">
              Wszystko, co Ci się spodobało — w jednym miejscu. Wróć do hodowli, sprawdź mioty, edytuj wyszukiwania.
            </p>
          </div>

          {/* Push Notifications Toggle */}
          <div className="mb-6">
            <PushToggle variant="card" />
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-white/10 -mx-2 px-2 overflow-x-auto" role="tablist">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = activeTab === t.key;
              const count = counts[t.key] || 0;
              return (
                <button
                  key={t.key}
                  onClick={() => switchTab(t.key)}
                  role="tab"
                  aria-selected={isActive}
                  className={`relative px-4 py-3 text-sm font-medium inline-flex items-center gap-2 whitespace-nowrap transition ${
                    isActive ? 'text-white' : 'text-gray-400 hover:text-gray-200'
                  }`}
                  data-testid={`obserwowane-tab-${t.key}`}
                >
                  <Icon className={`w-4 h-4 ${t.key === 'kennels' && isActive ? 'fill-rose-400 text-rose-400' : ''}`} />
                  {t.label}
                  {count > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      isActive ? 'bg-white text-black' : 'bg-white/10 text-gray-300'
                    }`}>
                      {count}
                    </span>
                  )}
                  {isActive && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-white rounded-full" />}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="py-20 flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
              <p className="text-sm text-gray-500">Ładuję…</p>
            </div>
          ) : (
            <>
              {activeTab === 'kennels' && (
                favs.kennels.length === 0 ? (
                  <EmptyState
                    icon={Heart}
                    title="Nie masz jeszcze zapisanych hodowli"
                    copy="Wejdź do katalogu, znajdź hodowlę która Ci pasuje i kliknij ❤️ — wróci tutaj."
                    ctaLabel="Przeglądaj hodowle"
                    ctaTo="/hodowle"
                    testId="obserwowane-empty-kennels"
                  />
                ) : (
                  <div className="space-y-3" data-testid="obserwowane-list-kennels">
                    {favs.kennels.map(item => <CardKennel key={item.slug} item={item} />)}
                  </div>
                )
              )}

              {activeTab === 'litters' && (
                (favs.litters.length + favs.studs.length) === 0 ? (
                  <EmptyState
                    icon={Dog}
                    title="Nie masz jeszcze zapisanych ogłoszeń"
                    copy="Zobacz aktualne mioty i reproduktory. Kliknij ❤️ przy każdym, który Cię zainteresuje."
                    ctaLabel="Znajdź szczenięta"
                    ctaTo="/szczenieta"
                    testId="obserwowane-empty-litters"
                  />
                ) : (
                  <div className="space-y-3" data-testid="obserwowane-list-litters">
                    {favs.litters.map(item => <CardLitter key={item.slug} item={item} />)}
                    {favs.studs.map(item => <CardLitter key={item.slug} item={item} />)}
                  </div>
                )
              )}

              {activeTab === 'searches' && (
                searches.length === 0 ? (
                  <EmptyState
                    icon={Bell}
                    title="Nie obserwujesz jeszcze żadnych wyszukiwań"
                    copy="Wyszukaj rasę i miasto, a następnie zapisz kryteria — powiadomimy Cię o nowych ofertach."
                    ctaLabel="Wyszukaj rasę"
                    ctaTo="/katalog"
                    testId="obserwowane-empty-searches"
                  />
                ) : (
                  <div className="space-y-3" data-testid="obserwowane-list-searches">
                    {searches.map(s => <CardSearch key={s.id} search={s} onDelete={handleDeleteSearch} />)}
                  </div>
                )
              )}
            </>
          )}

          <div className="mt-10 rounded-xl bg-white/[0.03] border border-white/10 p-5 text-sm text-gray-300">
            <strong className="text-white">Anonimowy zapis:</strong> Twoje obserwowane są zapisane lokalnie (per przeglądarka). Aby mieć je dostępne na innych urządzeniach — załóż konto.
            <button
              onClick={() => navigate('/rejestracja')}
              className="ml-2 underline text-blue-300 hover:text-blue-200"
              data-testid="obserwowane-create-account"
            >
              Utwórz konto
            </button>
          </div>
        </section>

        <ApisAIFooter pageType="account" />
      </div>
    </>
  );
}
